import { useCallback, useState } from 'react';
import { registerWithEmail } from '../application/authCredentials';
import {
  normalizeEmail,
  validateSignupInput,
  type AuthFormValidationResult,
} from '../application/authFormValidation';
import { useAuthStore } from '../store/useAuthStore';
import { showInfo, showError } from '../../shared/ui/appToast';
import { devLog } from '../../shared/dev/devLog';

function applyValidationFailure(result: Extract<AuthFormValidationResult, { ok: false }>) {
  useAuthStore.getState().setError(result.error);
  if (result.toast.kind === 'info') {
    showInfo(result.toast.title, result.toast.body);
  } else {
    showError(result.toast.title, result.toast.body);
  }
}

type Options = {
  email: string;
  password: string;
  passwordConfirm: string;
};

/**
 * Hook de signup: valida email/contraseña/confirmación, dispara
 * `registerWithEmail` y mantiene el estado local de "confirmación de correo
 * pendiente" (cuando Auth no devuelve sesión).
 */
export function useSignupForm({ email, password, passwordConfirm }: Options) {
  const [confirmationPendingEmail, setConfirmationPendingEmail] = useState<string | null>(null);

  const handleSignup = useCallback(async () => {
    const result = validateSignupInput(email, password, passwordConfirm);
    if (!result.ok) {
      applyValidationFailure(result);
      return;
    }
    try {
      const outcome = await registerWithEmail(normalizeEmail(email), password);
      if (outcome === 'pending_email_confirmation') {
        setConfirmationPendingEmail(normalizeEmail(email));
      }
    } catch (err) {
      devLog('[LoginScreen] Error signup', err);
    }
  }, [email, password, passwordConfirm]);

  const clearConfirmationPending = useCallback(() => {
    setConfirmationPendingEmail(null);
  }, []);

  return {
    handleSignup,
    confirmationPendingEmail,
    clearConfirmationPending,
  };
}
